import { type Analysis } from './analyze';
import { catOf, type PartLookup } from './catalog';
import type { Item, TextItem } from './drawing';
import { SHEET } from './geometry';
import { t as $t } from './i18n';
import { formatPlcAddress, parsePlcAddress, plcList, type PlcKind } from './plc';
import type { Placed } from './types';
import type { Project } from './types';

/*
 * PLC overview: every PLC card drawn as a box with all its channels, as EPLAN's PLC overview shows it. Inside the box
 * the address; on its right what the channel is (description, symbol, the device it is wired to); on its left the
 * cross-reference to the page the channel is drawn on. Four columns to a sheet; a card longer than a column goes on
 * in the next one.
 */

const COLS = 4, COLW = (SHEET.X1 - SHEET.X0) / COLS;
const TOP = 26, ROW = 6, HEAD = 13, BOTTOM = SHEET.TB_Y - 4;
/** Channel rows in one column under the card heading. */
export const PLC_OVERVIEW_ROWS = Math.floor((BOTTOM - TOP - HEAD - 2) / ROW);

/** A channel row: drawn, or free on the card (no symbol for it in the schematic). */
interface ChannelRow {
  addr: string;
  kind: string;
  sym: string;
  desc: string;
  wired: string;
  where: string;
  it?: Placed;
}

interface Column {
  card: string;
  part: string;
  rows: ChannelRow[];
  /** the card goes on from the previous column */
  cont: boolean;
}

/**
 * The cards cut into columns of at most PLC_OVERVIEW_ROWS channels, in card and address order. A card of one kind
 * whose part gives its channel count lists all of them: the ones not drawn as free.
 */
export function plcOverviewColumns(A: Analysis, parts: PartLookup = catOf): Column[] {
  // generator controllers are not PLC cards (their channels are the controller's own terminals)
  const list = plcList(A).filter((r) => A.mains.get(A.devKey.get(r.it.s.id) ?? '')?.[0]?.s.type !== 'gctl');
  const cards = new Map<string, typeof list>();
  for (const r of list) (cards.get(r.cells[0]) ?? cards.set(r.cells[0], []).get(r.cells[0])!).push(r);
  const out: Column[] = [];
  for (const [card, drawn] of cards) {
    const part = drawn.find((r) => r.cells[6])?.cells[6] ?? '';
    let rows: ChannelRow[] = drawn.map((r) => ({ addr: r.cells[1], kind: r.cells[2], sym: r.cells[3], desc: r.cells[4], wired: r.cells[5], where: r.cells[7], it: r.it }));
    const ch = part ? parts(part)?.ch : undefined, kinds = new Set(rows.map((r) => r.kind));
    const first = parsePlcAddress(rows[0].addr);
    if (ch && kinds.size === 1 && first && rows.length < ch) {
      // the card's addresses from its first byte (bits of whole bytes, or words two bytes apart)
      const kind = rows[0].kind as PlcKind, start = Math.min(...rows.map((r) => parsePlcAddress(r.addr)?.byte ?? first.byte));
      const used = new Map(rows.map((r) => [r.addr, r]));
      const all = Array.from({ length: ch }, (_, k) =>
        formatPlcAddress(first.word ? { ...first, byte: start + 2 * k, bit: 0 } : { ...first, byte: start + Math.floor(k / 8), bit: k % 8 }));
      // a drawn channel outside that range (addressed by hand) is kept, after them
      rows = [...all.map((addr) => used.get(addr) ?? { addr, kind, sym: '', desc: '', wired: '', where: '' }), ...rows.filter((r) => !all.includes(r.addr))];
    }
    for (let i = 0; i < rows.length; i += PLC_OVERVIEW_ROWS) out.push({ card, part, rows: rows.slice(i, i + PLC_OVERVIEW_ROWS), cont: i > 0 });
  }
  return out;
}

/** Sheets the overview needs (at least one). */
export const plcOverviewPages = (A: Analysis, parts: PartLookup = catOf) => Math.max(1, Math.ceil(plcOverviewColumns(A, parts).length / COLS));

/** The drawing of one sheet of the overview (the title block is drawn by the caller). */
export function plcOverviewItems(pr: Project, A: Analysis, part: number, parts: PartLookup = catOf): Item[] {
  void pr;
  const out: Item[] = [];
  const text = (x: number, y: number, s: string, o: Partial<TextItem> = {}) =>
    out.push({ k: 'text', x, y, size: 2.4, text: s, anchor: 'start', font: 'ui', colour: 'ink', layer: 'label', ...o });
  const poly = (pts: [number, number][], closed = false, width = 0.35) => out.push({ k: 'poly', pts, closed, stroke: 'ink', width, layer: 'symbol' });
  // a text cut to a width (about half the text size per character, a monospaced one a little more)
  const fit = (s: string, w: number, size: number, mono: boolean) => {
    const n = Math.max(1, Math.floor(w / (size * (mono ? 0.62 : 0.48))));
    return s.length > n ? s.slice(0, Math.max(1, n - 1)) + '…' : s;
  };
  text(SHEET.X0 + 4, 20, $t('PLC genel bakış'), { size: 5, font: 'cond', bold: true });
  const cols = plcOverviewColumns(A, parts).slice(part * COLS, (part + 1) * COLS);
  if (!cols.length) text(SHEET.X0 + 5, TOP + 6, $t('Projede PLC kanalı yok.'), { colour: 'ink2' });
  cols.forEach((c, i) => {
    const x0 = SHEET.X0 + i * COLW + 3;
    // the card: tag, part number and what the part is
    const pn = c.part, p = pn ? parts(pn) : undefined;
    text(x0, TOP + 4, c.card + (c.cont ? ` ${$t('(devam)')}` : ''), { size: 3.5, font: 'mono', bold: true, colour: 'ink' });
    if (pn) text(x0, TOP + 8.5, fit(pn, COLW - 8, 2.4, true), { font: 'mono', colour: 'ink2' });
    if (p?.d) text(x0, TOP + 12, fit($t(p.d), COLW - 8, 2.2, false), { size: 2.2, colour: 'ink2' });
    // the box with an address per row; pins out to the left, the channel's meaning on the right
    const bx0 = x0 + 17, bx1 = bx0 + 15, y0 = TOP + HEAD + 2, y1 = y0 + c.rows.length * ROW + 1;
    poly([[bx0, y0], [bx1, y0], [bx1, y1], [bx0, y1]], true);
    c.rows.forEach((r, k) => {
      const y = y0 + 0.5 + k * ROW + ROW / 2;
      const { addr, kind, sym, desc, wired, where, it } = r;
      poly([[bx0 - 5, y], [bx0, y]], false, 0.25);
      if (it) text(bx0 - 6, y + 0.85, where, { anchor: 'end', font: 'mono', size: 2.3, colour: 'accent', link: it.pi, layer: 'ref' });
      text((bx0 + bx1) / 2, y + 0.85, addr, { anchor: 'middle', font: 'mono', size: 2.4, bold: !!it, colour: it ? 'ink' : 'ink2' });
      text(bx0 + 1, y - 1.4, kind, { font: 'mono', size: 1.6, colour: 'ink2', layer: 'pin' });
      const w = COLW - (bx1 - x0) - 4;
      text(bx1 + 1.5, y - 0.4, it ? fit(desc || sym || '—', w, 2.3, false) : $t('boş'), { size: 2.3, layer: 'desc', ...(it ? {} : { colour: 'ink2' as const }) });
      const sub = [sym && desc ? sym : '', wired ? '→ ' + wired : ''].filter(Boolean).join('  ');
      if (sub) text(bx1 + 1.5, y + 2.2, fit(sub, w, 1.9, true), { size: 1.9, font: 'mono', colour: 'ink2', layer: 'desc' });
      if (k) out.push({ k: 'poly', pts: [[bx1 + 1, y - ROW / 2], [x0 + COLW - 4, y - ROW / 2]], stroke: 'frame', width: 0.1, layer: 'frame' });
    });
  });
  // column rules
  for (let i = 1; i < cols.length; i++) {
    const x = SHEET.X0 + i * COLW;
    out.push({ k: 'poly', pts: [[x, TOP - 2], [x, BOTTOM]], stroke: 'frame', width: 0.15, layer: 'frame' });
  }
  return out;
}
